import styled from "@emotion/styled";
import { pick } from "es-toolkit";
import { useRef, useState, type ReactNode } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { SkiliticsIcon } from "../../icons";
import { transientOptions } from "../../utilities";
import {
  FieldText,
  FileDropzone,
  UploadedMediaRow,
  useCommitChange,
  useFieldMediaAssetInputProps,
  useMediaAssetUploadFileDropzoneProps,
  type FileMediaAssetValue,
  type MediaAssetSource,
  type MediaAssetType,
  type MediaAssetValue,
  type UrlMediaAssetValue,
} from "../controls";
import { FormField } from "../FormField";
import { useFieldContext } from "../FormFieldContext";
import { iconByMediaAssetType, maximumFilesizeByMediaAssetType, uploadOptionsByMediaAssetType } from "./constants";
import { MediaAssetPopover } from "./MediaAssetPopover";
import { MediaAssetSourceMenu } from "./MediaAssetSourceMenu";
import { ToggleBackgroundPropertiesRow } from "./ToggleBackgroundPropertiesRow";

const Container = styled.div``;

const ChooseContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  height: 32px;
`;

const IconContainer = styled("div", transientOptions)<{ $clickable?: boolean }>`
  padding-inline-end: 6px;
  cursor: ${({ $clickable = false }) => ($clickable ? "pointer" : "default")};
`;

const DropContainer = styled.div`
  width: 100%;
`;

const OptionsContainer = styled.div``;

interface Props {
  assetType: MediaAssetType;
  showAiBadge?: boolean;
  settingsForm?: ReactNode;
  noClear?: boolean;
  hideAssetTypeIcon?: boolean;
  renderPreview: () => ReactNode;
}

export const FieldPreviewableMediaAsset = ({
  assetType,
  showAiBadge,
  settingsForm,
  noClear,
  hideAssetTypeIcon,
  renderPreview,
}: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { name } = useFieldContext();
  // NOTE: normal onChange doesn't work with object values so we have to use setValue instead
  const { value, setValue } = useFieldMediaAssetInputProps();

  const [uploading, setUploading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
  const commitChange = useCommitChange();

  const source = (value && "url" in value ? "url" : "file") satisfies MediaAssetSource;
  const fileDropzoneProps = useMediaAssetUploadFileDropzoneProps(setValue, {
    // we don't want the measurer to run as we will do it higher in stack (which also works for urls)
    ...pick(uploadOptionsByMediaAssetType[assetType], ["mediaTypes", "mimeType", "transformFile"]),
    onFileSelected: () => {
      setUploading(true);
      return {}; // TODO: remove this from api when migrated over. this was used to extract extra properties like width/height that should be done higher in stack
    },
  });

  const popoverRef = useRef<HTMLDivElement>(null);

  const handleSelectedSourceChange = (newSource: MediaAssetSource) => {
    if (newSource === "file") {
      void setValue({ source: "file", assetId: "", name: "" });
    } else if (newSource === "url") {
      void setValue({ source: "url", url: "" });
    }
  };

  const maximumFileSize = maximumFilesizeByMediaAssetType[assetType];

  const icon = source === "url" ? "link" : iconByMediaAssetType[assetType];

  if (value || Boolean(anchorEl)) {
    return (
      <Container ref={popoverRef}>
        <ToggleBackgroundPropertiesRow onRemove={noClear ? undefined : () => setValue(undefined)} toggle="none">
          {isUrl(value) && (
            <ChooseContainer>
              <IconContainer $clickable onClick={() => setAnchorEl(popoverRef.current)}>
                <SkiliticsIcon icon={icon} size={16} />
              </IconContainer>
              <FormField name={`${name}.url`}>
                <FieldText placeholder={formatMessage("mediaAssetUrlPlaceholder")} style={{ width: "100%" }} />
              </FormField>
            </ChooseContainer>
          )}
          {isFile(value) && (
            <UploadedMediaRow
              type={assetType}
              media={value}
              showAiBadge={showAiBadge}
              clickable
              onClick={() => setAnchorEl(popoverRef.current)}
            />
          )}
        </ToggleBackgroundPropertiesRow>

        <MediaAssetPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          header={formatMessage(assetType)}
          onClose={() => setAnchorEl(null)}
          settingsForm={settingsForm}
          renderPreview={renderPreview}
        />
      </Container>
    );
  }

  return (
    <Container>
      <ChooseContainer>
        {!hideAssetTypeIcon && <IconContainer>{!uploading && <SkiliticsIcon icon={icon} size={16} />}</IconContainer>}
        {source === "file" && (
          <DropContainer>
            <FileDropzone
              {...fileDropzoneProps}
              maximumFileSize={maximumFileSize}
              onFileProcessed={() => {
                fileDropzoneProps?.onFileProcessed?.();
                setUploading(false);
                commitChange();
              }}
            />
          </DropContainer>
        )}
        <OptionsContainer>
          {!uploading && <MediaAssetSourceMenu source={source} onSourceChanged={handleSelectedSourceChange} />}
        </OptionsContainer>
      </ChooseContainer>
    </Container>
  );
};

const formatStrings = {
  audio: {
    en: "Audio File",
  },
  image: {
    en: "Image File",
  },
  model: {
    en: "Model File",
  },
  video: {
    en: "Video File",
  },
  mediaAssetUrlPlaceholder: {
    en: "Enter URL",
  },
} satisfies FormatMessages;

const isUrl = (value?: MediaAssetValue): value is UrlMediaAssetValue => !!value && "url" in value;

const isFile = (value?: MediaAssetValue): value is FileMediaAssetValue => !!value && "assetId" in value;
